import React, { useState, useEffect } from 'react';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Total count of slides (0 to 7)
  const totalSlides = 8;

  // 1. Automatic Scrolling Effect
  useEffect(() => {
    // Pause auto-scroll if user is currently inspecting a dropdown
    if (activeTooltip !== null) return;

    const autoScrollTimer = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % totalSlides);
    }, 4000); // Changes slide every 4 seconds

    return () => clearInterval(autoScrollTimer);
  }, [activeTooltip]);

  // 2. Manual Keyboard Navigation Support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      } else if (e.key === 'ArrowLeft') {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const slides = [
    { id: 'slide1', name: 'Start' },
    { id: 'slide2', name: 'Philosophy' },
    { id: 'slide3', name: 'Capabilities' },
    { id: 'slide4', name: 'Tech Matrix' },
    { id: 'slide5', name: 'Engineering' },
    { id: 'slide6', name: 'Metrics' },
    { id: 'slide7', name: 'Tiers' },
    { id: 'slide8', name: 'Contact' }
  ];

  const handleNavClick = (index) => {
    setCurrentSlide(index);
    setActiveTooltip(null); // Clear open dropdowns on slide change
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setActiveTooltip(null);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setActiveTooltip(null);
  };

  return (
    <div className="presentation-viewport">
      {/* Dynamic Font Awesome and Google Fonts injected cleanly */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Styled Embed block */}
      <style dangerouslySetInnerHTML={{ __html: `
        .presentation-viewport {
          margin: 0;
          padding: 40px 20px;
          box-sizing: border-box;
          background-color: #0b0f19;
          color: #f8fafc;
          font-family: 'Inter', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
        }

        /* RESPONSIVE BASE CONTAINER FRAME - ACTS AS VIEWPORT WINDOW */
        .slides-frame {
          width: 100%;
          max-width: auto;
          height: 980px;
          background-color: #ffffff;
          color: #0f172a;
          border-radius: 16px;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.6);
          position: relative;
          overflow: hidden;
        }

        /* HARDWARE-ACCELERATED SLIDING TRACK */
        .slides-track {
          display: flex;
          width: 800%; /* 8 slides * 100% each */
          height: 100%;
          transition: transform 0.65s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .slide-wrapper {
          width: 12.5% !important; /* 100% divided by 8 slides */
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          padding: 60px 80px;
          box-sizing: border-box;
          position: relative;
          flex-shrink: 0;
        }

        /* Theme States */
        .slide-dark {
          background-color: #0f172a;
          color: #f8fafc;
        }

        /* Navigation Arrows Overlay Styles */
        .slide-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #ffffff;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 20;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .slide-arrow:hover {
          background: #2563eb;
          color: #ffffff;
          border-color: #2563eb;
          transform: translateY(-50%) scale(1.12);
        }

        .slide-arrow.arrow-left {
          left: 24px;
        }

        .slide-arrow.arrow-right {
          right: 24px;
        }

        /* Adjust hover color logic based on active slide theme context */
        .slide-dark-arrow-hover:hover {
          background: #38bdf8 !important;
          border-color: #38bdf8 !important;
          color: #0f172a !important;
        }

        /* Abstract Blurry Premium Backgrounds */
        .background-blob-rt {
          position: absolute;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, rgba(56, 189, 248, 0.02) 70%, transparent 100%);
          border-radius: 50%;
          top: -150px;
          right: -150px;
          z-index: 0;
          pointer-events: none;
        }

        .background-blob-lb {
          position: absolute;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.08) 0%, rgba(37, 99, 235, 0.01) 70%, transparent 100%);
          border-radius: 50%;
          bottom: -200px;
          left: -200px;
          z-index: 0;
          pointer-events: none;
        }

        /* Typography Styles */
        .slide-title {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 38px;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #0f172a;
          margin-bottom: 30px;
          border-left: 5px solid #2563eb;
          padding-left: 15px;
          line-height: 1.2;
          z-index: 1;
        }

        .slide-dark .slide-title {
          color: #ffffff;
          border-left-color: #38bdf8;
        }

        /* Global Header/Footer UI Details */
        .slide-header {
          position: absolute;
          top: 30px;
          left: 80px;
          right: 80px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          color: #94a3b8;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          font-weight: 600;
          z-index: 5;
        }

        .slide-logo {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 700;
        }

        .slide-logo span {
          color: #2563eb;
        }

        .slide-dark .slide-logo span {
          color: #38bdf8;
        }

        .slide-footer {
          position: absolute;
          bottom: 30px;
          left: 80px;
          right: 80px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #94a3b8;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          padding-top: 15px;
          z-index: 5;
        }

        .slide-dark .slide-footer {
          border-top-color: rgba(255, 255, 255, 0.05);
        }

        /* Slide layout styles */
        .content-area {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          justify-content: center;
          width: 100%;
          z-index: 1;
        }

        /* Slide Grid structures */
        .two-column {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          width: 100%;
        }

        .tiled-content {
          display: flex;
          gap: 30px;
          width: 100%;
          justify-content: stretch;
        }

        .tile {
          flex: 1;
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 35px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-sizing: border-box;
        }

        .slide-dark .tile {
          background-color: #1e293b;
          border-color: rgba(255, 255, 255, 0.08);
        }

        .tile:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px -10px rgba(37,99,235,0.15);
          border-color: #2563eb;
        }

        .tile .icon {
          font-size: 36px;
          color: #2563eb;
          margin-bottom: 20px;
        }

        .slide-dark .tile .icon {
          color: #38bdf8;
        }

        .tile h3 {
          font-size: 20px;
          color: #0f172a;
          margin-bottom: 12px;
          font-weight: 700;
        }

        .slide-dark .tile h3 {
          color: #ffffff;
        }

        .tile p {
          font-size: 14px;
          line-height: 1.6;
          color: #475569;
        }

        .slide-dark .tile p {
          color: #94a3b8;
        }

        /* Bullet Points with Interactive Inline Dropdown */
        .tooltip-bullet-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 15px;
          width: 100%;
        }

        .tooltip-bullet-item {
          background: #1e293b;
          border-left: 5px solid #38bdf8;
          border-radius: 0 12px 12px 0;
          padding: 18px 24px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: flex-start;
          box-sizing: border-box;
          width: 100%;
        }

        .tooltip-bullet-item:hover, .tooltip-bullet-item.active {
          background: #273549;
          transform: translateX(6px);
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.3);
        }

        .bullet-trigger-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .bullet-text {
          font-size: 16.5px;
          font-weight: 600;
          color: #f1f5f9;
          display: flex;
          align-items: center;
          gap: 12px;
          text-align: left;
        }

        .bullet-text i {
          color: #38bdf8;
          font-size: 16px;
        }

        .hover-prompt {
          font-size: 11.5px;
          color: #94a3b8;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .chevron-icon {
          transition: transform 0.3s ease;
          color: #38bdf8;
        }

        .tooltip-bullet-item:hover .chevron-icon, .tooltip-bullet-item.active .chevron-icon {
          transform: rotate(180deg);
        }

        /* Dropdown safe animation space */
        .tooltip-box {
          width: 100%;
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease, margin-top 0.3s ease;
          background: transparent;
          border: none;
          box-shadow: none;
          padding: 0;
          pointer-events: none;
        }

        .tooltip-bullet-item:hover .tooltip-box, .tooltip-bullet-item.active .tooltip-box {
          max-height: 120px;
          opacity: 1;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          pointer-events: auto;
        }

        .tooltip-box h4 {
          font-size: 14px;
          color: #38bdf8;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          text-align: left;
        }

        .tooltip-box p {
          font-size: 13px;
          line-height: 1.5;
          color: #cbd5e1;
          text-align: left;
        }

        /* Image Wrapper structure */
        .image-wrapper {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 40px -15px rgba(0,0,0,0.3);
          height: 400px;
          width: 100%;
          border: 1px solid rgba(0, 0, 0, 0.08);
        }

        .image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Key Metrics / KPI components */
        .kpi-container {
          display: flex;
          align-items: center;
          justify-content: space-around;
          width: 100%;
        }

        .kpi-card {
          text-align: center;
          padding: 20px;
        }

        .kpi-number {
          font-size: 96px;
          font-weight: 700;
          color: #2563eb;
          line-height: 1;
          font-family: 'Plus Jakarta Sans', sans-serif;
          margin-bottom: 12px;
        }

        .slide-dark .kpi-number {
          color: #38bdf8;
        }

        .kpi-label {
          font-size: 18px;
          font-weight: 600;
          color: #475569;
        }

        .slide-dark .kpi-label {
          color: #cbd5e1;
        }

        /* Enterprise Table elements */
        .table-container {
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
        }

        th {
          background-color: #f1f5f9;
          color: #0f172a;
          font-weight: 700;
          padding: 18px 24px;
          font-size: 15px;
        }

        td {
          padding: 18px 24px;
          border-bottom: 1px solid #e2e8f0;
          color: #475569;
          font-size: 14px;
        }

        tr:last-child td {
          border-bottom: none;
        }

        tr:hover td {
          background-color: #f8fafc;
        }

        /* CTA Interactive Elements */
        .cta-box {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .btn-primary {
          display: inline-block;
          background: #2563eb;
          color: white;
          padding: 16px 40px;
          font-size: 18px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s ease;
          margin-top: 30px;
          box-shadow: 0 10px 20px -5px rgba(37,99,235,0.3);
          border: none;
          cursor: pointer;
        }

        .btn-primary:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 15px 25px -5px rgba(37,99,235,0.4);
        }

        .slide-dark .btn-primary {
          background: #38bdf8;
          color: #0f172a;
          box-shadow: 0 10px 20px -5px rgba(56,189,248,0.3);
        }

        .slide-dark .btn-primary:hover {
          background: #7dd3fc;
          box-shadow: 0 15px 25px -5px rgba(56,189,248,0.4);
        }

        /* Slide Footer dots */
        .dot-nav {
          display: flex;
          gap: 8px;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #cbd5e1;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .slide-dark .dot {
          background-color: #475569;
        }

        .dot.active {
          background-color: #2563eb;
        }

        .slide-dark .dot.active {
          background-color: #38bdf8;
        }

        /* ==========================================================================
           MEDIA QUERY SYSTEM FOR PREMIUM RESPONSIVENESS (MOBILE/TABLET DEEP-DIVE)
           ========================================================================== */
        @media (max-width: 1024px) {
          .slides-frame {
            height: 680px !important;
            max-height: none !important;
            min-height: auto !important;
            box-shadow: none;
            border-radius: 12px;
          }

          .slide-wrapper {
            height: 100% !important;
            padding: 40px 24px 80px 24px !important;
          }

          /* Relocate slide headers to top static flows */
          .slide-header {
            position: relative;
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .slide-title {
            font-size: 28px;
            margin-bottom: 25px;
          }

          /* Convert side layouts into stacking grids */
          .two-column {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
            width: 100% !important;
          }

          .tiled-content {
            flex-direction: column !important;
            gap: 20px !important;
            width: 100% !important;
          }

          .tile {
            width: 100% !important;
            padding: 20px !important;
          }

          .image-wrapper {
            width: 100% !important;
            height: 240px !important;
          }

          .tooltip-bullet-list {
            width: 100% !important;
          }

          .kpi-container {
            flex-direction: column !important;
            gap: 20px !important;
            width: 100% !important;
          }

          .kpi-card {
            width: 100% !important;
            padding: 5px 0 !important;
          }

          .kpi-number {
            font-size: 54px !important;
          }

          /* Table scrolling guard to prevent system spills on screens */
          .table-container {
            width: 100% !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }

          table {
            min-width: 600px; /* Forces safety row width */
          }

          th, td {
            padding: 10px 14px !important;
            font-size: 13px !important;
          }

          /* Re-position static slide footers elegantly below scrolled margins */
          .slide-footer {
            position: absolute !important;
            bottom: 20px !important;
            left: 24px !important;
            right: 24px !important;
            width: calc(100% - 48px) !important;
            border-top: 1px solid rgba(0, 0, 0, 0.08) !important;
            padding-top: 12px !important;
            flex-direction: row !important;
            gap: 15px !important;
            align-items: center !important;
          }

          .slide-dark .slide-footer {
            border-top-color: rgba(255, 255, 255, 0.08) !important;
          }

          .dot-nav {
            justify-content: flex-end;
          }

          .slide-arrow {
            width: 40px;
            height: 40px;
          }
          .slide-arrow.arrow-left {
            left: 10px;
          }
          .slide-arrow.arrow-right {
            right: 10px;
          }
        }

        @media (max-width: 640px) {
          .slide-title {
            font-size: 22px;
          }

          .slide-wrapper {
            padding: 30px 16px 60px 16px !important;
          }

          h1 {
            font-size: 36px !important;
          }

          .btn-primary {
            width: 100%;
            text-align: center;
            box-sizing: border-box;
          }

          .slide-footer {
            display: none !important; /* Hide footer lines on small screens to fit content track sliding */
          }
        }
      ` }} />

      {/* DYNAMIC SCALING PRESENTATION FRAME */}
      <div className="slides-frame">
        
        {/* Navigation Arrows */}
        <button 
          className={`slide-arrow arrow-left ${(currentSlide === 0 || currentSlide === 3 || currentSlide === 5 || currentSlide === 7) ? 'slide-dark-arrow-hover' : ''}`}
          onClick={handlePrev}
          aria-label="Previous Slide"
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <button 
          className={`slide-arrow arrow-right ${(currentSlide === 0 || currentSlide === 3 || currentSlide === 5 || currentSlide === 7) ? 'slide-dark-arrow-hover' : ''}`}
          onClick={handleNext}
          aria-label="Next Slide"
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>

        {/* Sliding Track */}
        <div className="slides-track" style={{ transform: `translateX(-${currentSlide * 12.5}%)` }}>
          
          {/* SLIDE 1: INTRO (Dark Theme) */}
          <div className="slide-wrapper slide-dark">
            <div className="background-blob-rt"></div>
            <div className="background-blob-lb"></div>
            
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Strategic Architecture</div>
            </div>

            <div className="content-area" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
              <h1 style={{ fontSize: '64px', lineHeight: '1.1', marginBottom: '24px', fontWeight: '800', letterSpacing: '-0.03em' }}>
                Empowering Digital Innovation
              </h1>
              <p style={{ fontSize: '21px', lineHeight: '1.6', color: '#cbd5e1', marginBottom: '40px', fontWeight: '300' }}>
                NirvanaSol helps modern enterprises transform with performant custom software, secure cloud architecture, and data-driven marketing.
              </p>
              <div>
                <button onClick={() => handleNavClick(1)} className="btn-primary">Explore Our Capabilities</button>
              </div>
            </div>

            <div className="slide-footer">
              <div>© 2026 NirvanaSol Inc. All rights reserved.</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 2: THE PHILOSOPHY */}
          <div className="slide-wrapper">
            <div className="background-blob-lb" style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.04) 0%, transparent 70%)' }}></div>
            
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Engineering Philosophy</div>
            </div>

            <h2 className="slide-title">Our Operational Philosophy</h2>
            
            <div className="content-area">
              <div className="two-column">
                <div className="tile" style={{ padding: '45px', height: '100%' }}>
                  <div className="icon"><i className="fa-solid fa-gears"></i></div>
                  <h3>Engineered to Scale</h3>
                  <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#475569' }}>
                    We construct backend systems using modern, modular microservice architectures. By separating critical processes, your software platform scales seamlessly under massive transaction loads.
                  </p>
                </div>
                
                <div className="tile" style={{ padding: '45px', height: '100%' }}>
                  <div className="icon"><i className="fa-solid fa-bullseye"></i></div>
                  <h3>Business Goal Alignment</h3>
                  <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#475569' }}>
                    We don't build tech for the sake of tech. Our engineers analyze your exact business KPIs and configure technical capabilities that directly impact operational efficiency and growth.
                  </p>
                </div>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 01 // Vision &amp; Approach</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 3: CORE CAPABILITIES */}
          <div className="slide-wrapper">
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Our Capabilities</div>
            </div>

            <h2 className="slide-title">Strategic Core Offerings</h2>

            <div className="content-area">
              <div className="tiled-content">
                <div className="tile">
                  <div className="icon"><i className="fa-solid fa-laptop-code"></i></div>
                  <h3>Custom Software</h3>
                  <p>High-fidelity, responsive mobile &amp; web interfaces backed by modern databases. Designed for longevity and optimal usability.</p>
                </div>

                <div className="tile">
                  <div className="icon"><i className="fa-solid fa-cloud-arrow-up"></i></div>
                  <h3>Cloud Solutions</h3>
                  <p>High-availability cloud engineering, serverless migrations, and continuous integration pipelines designed for top-tier security.</p>
                </div>

                <div className="tile">
                  <div className="icon"><i className="fa-solid fa-chart-line"></i></div>
                  <h3>Digital Marketing</h3>
                  <p>Hyper-targeted online growth, optimized search performance (SEO), and dynamic audience outreach campaigns.</p>
                </div>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 02 // Capability Spectrum</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 4: TECHNICAL COMPLIANCE MATRIX */}
          <div className="slide-wrapper slide-dark">
            <div className="background-blob-rt" style={{ top: '-250px', right: '-250px', opacity: 0.7 }}></div>
            
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Interactive Insight</div>
            </div>

            <h2 className="slide-title">Technical Competence Matrix</h2>

            <div className="content-area">
              <div className="two-column">
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '28px', marginBottom: '20px', fontFamily: 'Plus Jakarta Sans' }}>Precision Architecture</h3>
                  <p style={{ color: '#94a3b8', fontSize: '15px', lineHeight: '1.6', marginBottom: '30px' }}>
                    Hover over or tap each list element on the right to open the system blueprint dropdown and inspect structural details.
                  </p>
                  <div style={{ background: 'rgba(56, 189, 248, 0.05)', border: '1px dashed rgba(56, 189, 248, 0.2)', padding: '20px', borderRadius: '8px' }}>
                    <span style={{ color: '#38bdf8', fontWeight: 600, fontSize: '14px' }}><i className="fa-solid fa-info-circle"></i> USER FRIENDLY CONTROL:</span>
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: '5px 0 0 0' }}>Expanding an accordion item pauses the automatic slide progression so you can study the architecture comfortably.</p>
                  </div>
                </div>

                <div>
                  <ul className="tooltip-bullet-list">
                    {/* BULLET 1 */}
                    <li 
                      className={`tooltip-bullet-item ${activeTooltip === 1 ? 'active' : ''}`}
                      onMouseEnter={() => setActiveTooltip(1)}
                      onMouseLeave={() => setActiveTooltip(null)}
                      onClick={() => setActiveTooltip(activeTooltip === 1 ? null : 1)}
                    >
                      <div className="bullet-trigger-row">
                        <span className="bullet-text">
                          <i className="fa-solid fa-terminal"></i> Custom Web &amp; Mobile Solutions
                        </span>
                        <span className="hover-prompt">
                          {activeTooltip === 1 ? 'Collapse ' : 'Expand '} 
                          <i className="fa-solid fa-chevron-down chevron-icon"></i>
                        </span>
                      </div>
                      
                      <div className="tooltip-box">
                        <h4>React &amp; Native Core</h4>
                        <p>We craft lightning-fast apps leveraging reusable component UI, solid centralized state management (Redux/Zustand), and offline syncing capability.</p>
                      </div>
                    </li>

                    {/* BULLET 2 */}
                    <li 
                      className={`tooltip-bullet-item ${activeTooltip === 2 ? 'active' : ''}`}
                      onMouseEnter={() => setActiveTooltip(2)}
                      onMouseLeave={() => setActiveTooltip(null)}
                      onClick={() => setActiveTooltip(activeTooltip === 2 ? null : 2)}
                    >
                      <div className="bullet-trigger-row">
                        <span className="bullet-text">
                          <i className="fa-solid fa-server"></i> Scalable Cloud Infrastructure
                        </span>
                        <span className="hover-prompt">
                          {activeTooltip === 2 ? 'Collapse ' : 'Expand '} 
                          <i className="fa-solid fa-chevron-down chevron-icon"></i>
                        </span>
                      </div>
                      
                      <div className="tooltip-box">
                        <h4>AWS / Azure Deployments</h4>
                        <p>Automated deployments featuring secure VPC configurations, continuous monitoring, auto-scaling thresholds, and robust DDoS shielding.</p>
                      </div>
                    </li>

                    {/* BULLET 3 */}
                    <li 
                      className={`tooltip-bullet-item ${activeTooltip === 3 ? 'active' : ''}`}
                      onMouseEnter={() => setActiveTooltip(3)}
                      onMouseLeave={() => setActiveTooltip(null)}
                      onClick={() => setActiveTooltip(activeTooltip === 3 ? null : 3)}
                    >
                      <div className="bullet-trigger-row">
                        <span className="bullet-text">
                          <i className="fa-solid fa-gauge-high"></i> High-Performance Optimization
                        </span>
                        <span className="hover-prompt">
                          {activeTooltip === 3 ? 'Collapse ' : 'Expand '} 
                          <i className="fa-solid fa-chevron-down chevron-icon"></i>
                        </span>
                      </div>
                      
                      <div className="tooltip-box">
                        <h4>Engine-level Speed</h4>
                        <p>SQL optimization, Redis in-memory caching tiers, and CDN integration designed to drop visual latency below 200 milliseconds globally.</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 03 // Integration Blueprint</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 5: CUSTOM SOFTWARE DEEP DIVE */}
          <div className="slide-wrapper">
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Core Development</div>
            </div>

            <h2 className="slide-title">Engineered For Performance</h2>

            <div className="content-area">
              <div className="two-column">
                <div>
                  <h3 style={{ fontSize: '28px', color: '#0f172a', marginBottom: '20px', fontFamily: 'Plus Jakarta Sans' }}>Bespoke Software Design</h3>
                  <p style={{ color: '#475569', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px' }}>
                    We develop custom applications built to withstand modern business requirements. Avoid the limitations of generic SaaS templates and gain complete ownership of your technology pipeline.
                  </p>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', color: '#334155' }}>
                    <li style={{ fontSize: '14.5px' }}><i className="fa-solid fa-circle-check" style={{ color: '#2563eb', marginRight: '8px' }}></i> Clean, testable backend architectures</li>
                    <li style={{ fontSize: '14.5px' }}><i className="fa-solid fa-circle-check" style={{ color: '#2563eb', marginRight: '8px' }}></i> Native responsiveness for iOS, Android, and Desktop</li>
                    <li style={{ fontSize: '14.5px' }}><i className="fa-solid fa-circle-check" style={{ color: '#2563eb', marginRight: '8px' }}></i> Comprehensive data encryption protocols</li>
                  </ul>
                </div>
                
                <div>
                  <div className="image-wrapper">
                    <img alt="Software Engineering Workspace" src="http://googleusercontent.com/image_collection/image_retrieval/7802818433157864407_0" />
                  </div>
                </div>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 04 // Software Deep-Dive</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 6: METRICS & IMPACT */}
          <div className="slide-wrapper slide-dark">
            <div className="background-blob-lb" style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 60%)' }}></div>
            
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Proven Business Impact</div>
            </div>

            <h2 className="slide-title">System Reliability &amp; Impact</h2>

            <div className="content-area">
              <div className="kpi-container">
                <div className="kpi-card">
                  <div className="kpi-number">99.99%</div>
                  <div className="kpi-label">Target Uptime Achieved</div>
                </div>
                
                <div className="kpi-card">
                  <div className="kpi-number">40%</div>
                  <div className="kpi-label">Infrastructure Cost Reduction</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-number">3x</div>
                  <div className="kpi-label">Client User Engagement Growth</div>
                </div>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 05 // Reliability Metrics</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 7: COMPARISON */}
          <div className="slide-wrapper">
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Service Structure</div>
            </div>

            <h2 className="slide-title">Engagement Tiers Comparison</h2>

            <div className="content-area">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Capability Feature</th>
                      <th>Tier I: Basic Web App</th>
                      <th>Tier II: Cloud Infrastructure</th>
                      <th>Tier III: Full Transformation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Core Architecture</strong></td>
                      <td>Static React Build</td>
                      <td>Autoscaling Microservices</td>
                      <td>Custom Multitenant Enterprise</td>
                    </tr>
                    <tr>
                      <td><strong>Cloud Integration</strong></td>
                      <td>Basic Hosting (Vercel/S3)</td>
                      <td>Dedicated AWS Setup</td>
                      <td>Redundant Hybrid Infrastructure</td>
                    </tr>
                    <tr>
                      <td><strong>Security Audits</strong></td>
                      <td>Standard SSL</td>
                      <td>Automated Threat Checks</td>
                      <td>Continuous Pen-Testing</td>
                    </tr>
                    <tr>
                      <td><strong>Growth Management</strong></td>
                      <td>Basic SEO Setup</td>
                      <td>Campaign Analytics</td>
                      <td>Fully Integrated Digital Campaigning</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 06 // Service Matrix</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

          {/* SLIDE 8: CONTACT & CALL TO ACTION */}
          <div className="slide-wrapper slide-dark">
            <div className="background-blob-rt" style={{ top: '50%', left: '50%', transform: 'translate(-50%,-50%)', opacity: 0.5 }}></div>
            
            <div className="slide-header">
              <div className="slide-logo">Nirvana<span>Sol</span></div>
              <div>Accelerate Growth</div>
            </div>

            <div className="content-area">
              <div className="cta-box">
                <h2 style={{ fontSize: '52px', color: '#ffffff', marginBottom: '20px', fontWeight: '800', letterSpacing: '-0.02em', fontFamily: 'Plus Jakarta Sans' }}>
                  Ready to scale your next digital breakthrough?
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '18px', lineHeight: '1.6', maxWidth: '650px', margin: '0 auto' }}>
                  Let us design, develop, and market custom system platforms designed explicitly around your corporate operational metrics.
                </p>
                <a href="mailto:solutions@nirvanasol.com" className="btn-primary" style={{ textDecoration: 'none' }}>
                  Connect with our Lead Architect
                </a>
                
                <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'center', gap: '40px', color: '#cbd5e1', fontSize: '15px' }}>
                  <div><i className="fa-solid fa-envelope" style={{ color: '#38bdf8', marginRight: '8px' }}></i> sales@nirvanasol.com</div>
                  <div><i className="fa-solid fa-globe" style={{ color: '#38bdf8', marginRight: '8px' }}></i> www.nirvanasol.com</div>
                </div>
              </div>
            </div>

            <div className="slide-footer">
              <div>Section 07 // Strategic Partnership</div>
              <div className="dot-nav">
                {slides.map((_, idx) => (
                  <span key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} onClick={() => handleNavClick(idx)}></span>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}